<template>
  <sui-modal class="wrapper" size="small" v-model="visible" :closable="false" :style="{zIndex}">
    <sui-modal-header class="header">
      Update host
    </sui-modal-header>
    <sui-modal-content>
      <sui-form warning :error="hasApiErrors" :loading="loading" v-if="entity">
        <sui-segment emphasis="secondary" v-if="entity.isCloud">
          <sui-form-fields>
            <sui-form-field :width="8">
              <label>Provider</label>
              <sui-dropdown class="disabled" search selection :options="providersList" v-model="entity.provider" />
            </sui-form-field>
            <sui-form-field :width="8">
              <label>Region</label>
              <sui-dropdown class="disabled" search selection :options="regionsList" v-model="entity.region" />
            </sui-form-field>
          </sui-form-fields>
        </sui-segment>
        <sui-segment emphasis="secondary" v-if="visibleFields.instance">
          <sui-form-fields>
            <sui-form-field :width="11">
              <label>Instance</label>
              <sui-dropdown :class="{disabled: !actions.hostUpdate}" search selection :options="instancesList" v-model="entity.instance" />
            </sui-form-field>
            <sui-form-field :width="2" :disabled="visibleFields.instance.reversibleReadonly">
              <label>Reversible</label>
              <sui-checkbox :disabled="!actions.hostUpdate" toggle v-model="reversible" />
            </sui-form-field>
            <sui-form-field :width="3">
              <label style="opacity:0">.</label>
              <sui-button color="blue" fluid :disabled="inProgress || isSameInstance || !actions.hostUpdate" @click.prevent="updateInstance()">Resize</sui-button>
            </sui-form-field>
          </sui-form-fields>
          <sui-message size="tiny">
            <div v-show="!reversible">
              <div class="resize-icons">
                <i class="ui large fitted icons">
                  <i class="inverted circular microchip fitted icon" />
                  <i class="yellow inverted corner long arrow alternate up fitted icon" />
                </i>
                <i class="ui large fitted icons">
                  <i class="inverted circular disk fitted icon" />
                  <i class="yellow inverted corner long arrow alternate up fitted icon" />
                </i>
              </div>
              This operation will restart the host and increase its <b>disk size, CPU and RAM</b>.<br>
              <b>This is a permanent change and cannot be reversed.</b>
            </div>
            <div v-show="reversible">
              <div class="resize-icons">
                <i class="ui large fitted icons">
                  <i class="inverted circular microchip fitted icon" />
                  <i class="green inverted corner arrows alternate vertical fitted icon" />
                </i>
                <i class="ui large fitted icons">
                  <i class="inverted circular disk fitted icon" />
                  <i class="red inverted corner lock fitted icon" />
                </i>
              </div>
              This operation will restart the host and change its <b>CPU and RAM, but not disk size</b>.<br>
              <b>This can be reversed.</b>
            </div>
          </sui-message>
        </sui-segment>
        <sui-segment emphasis="secondary" v-if="visibleFields.extraStorage">
          <sui-form-fields>
            <sui-form-field :width="4">
              <label>Volume size</label>
              <sui-input type="number" v-model="entity.extraStorage" :min="minExtraStorage" :max="maxExtraStorage" :disabled="isExtraStorageDisabled" />
            </sui-form-field>
            <sui-form-field :width="9">
              <label>{{visibleFields.extraStorage.infoHeader}}</label>
              <div size="tiny">
                {{visibleFields.extraStorage.infoLine1}}<br>
                {{visibleFields.extraStorage.infoLine2}}
              </div>
            </sui-form-field>
            <sui-form-field :width="3">
              <label style="opacity:0">.</label>
              <sui-button color="blue" fluid :disabled="isResizeDisabled || !actions.hostUpdate" @click.prevent="updateDisk()">Resize</sui-button>
            </sui-form-field>
          </sui-form-fields>
          <sui-message size="mini" warning v-if="isExtraStorageDisabled">
            Only for hosts created since December 2017 with LVM support!
          </sui-message>
        </sui-segment>
        <sui-segment emphasis="secondary" v-if="visibleFields.keys">
          <sui-form-fields>
            <sui-form-field :width="11">
              <label>Public keys</label>
              <sui-dropdown search selection multiple :options="keysList" :class="{disabled: !keysList.length || !actions.hostUpdate}" :placeholder="keysList.length ? 'Select...' : 'No public keys'" v-model="entity.keys" />
            </sui-form-field>
            <sui-form-field :width="2">
              <label style="opacity:0">.</label>
              <sui-button class="icon" size="tiny" :disabled="!access.keyCreate" fluid compact data-inverted data-position="left center" data-tooltip="Add new key" @click.prevent="$modals.createKey.open()">
                <i class="ui large fitted icons">
                  <i class="key fitted icon" />
                  <i class="inverted corner plus fitted icon" />
                </i>
              </sui-button>
            </sui-form-field>
            <sui-form-field :width="3">
              <label style="opacity:0">.</label>
              <sui-button color="blue" fluid :disabled="inProgress || areKeysSame || !actions.hostUpdate" @click.prevent="saveKeys()">Update</sui-button>
            </sui-form-field>
          </sui-form-fields>
        </sui-segment>
        <sui-message error v-if="apiErrors">
          {{apiErrors}}
        </sui-message>
      </sui-form>
    </sui-modal-content>
    <sui-modal-actions>
      <sui-button-group>
        <sui-button icon="ban" label-position="right" color="red" @click="close()">Close</sui-button>
      </sui-button-group>
    </sui-modal-actions>
    <sui-dimmer class="dimmer" :active="success" inverted>
      <h2 class="ui icon header">
        <i class="green check circle icon" />
        Updating started
      </h2>
    </sui-dimmer>
  </sui-modal>
</template>

<script>
  import {mapGetters} from 'vuex'
  import cloneDeep from 'lodash/cloneDeep'
  import ModalForm from '@/mixins/ModalForm'
  import utils from '@/utils'

  export default {
    name: 'ModalUpdateHost',
    mixins: [
      ModalForm,
    ],
    data() {
      return {
        reversible: false,
      }
    },
    computed: {
      ...mapGetters(['access', 'actionsById', 'providers', 'keys']),
      actions() {
        return this.actionsById[this.entity.id]
      },
      keysList() {
        return this.keys.map(key => {
          return {
            icon: key.type === 'sftp' ? 'folder' : 'key',
            text:  key.name,
            value: key.id,
          }
        })
      },
      instancesList() {
        return this.entity.constructor._instances.filter(instance => instance.active).map(instance => {
          return {
            icon: 'grey microchip',
            text: instance._fullTitle,
            value: instance.id,
            disabled: instance.storageSize < this.entity.storageSize,
          }
        })
      },
      providersList() {
        return this.providers.filter(provider => provider.purpose === 'hosting').map(provider => {
          return {
            image: {src: provider._logo},
            text: provider.name,
            value: provider.id,
          }
        })
      },
      regionsList() {
        return this.entity.constructor._regions.map(region => {
          return {
            flag: region.country,
            text: region.city,
            value: region.id,
          }
        })
      },
      inProgress() {
        return this.entity._inProgress
      },
      visibleFields() {
        return this.entity.getFields('updating')
      },
      oldInstanceData() {
        return this.oldEntity._instanceData
      },
      newInstanceData() {
        return this.entity._instanceData
      },
      isSameInstance() {
        return this.oldInstanceData.memory === this.newInstanceData.memory && this.oldInstanceData.cores === this.newInstanceData.cores && this.oldInstanceData.storageSize === this.newInstanceData.storageSize
      },
      isIncreasing() {
        return this.newInstanceData.memory > this.oldInstanceData.memory || this.newInstanceData.memory > this.oldInstanceData.memory
      },
      isDecreasing() {
        return this.newInstanceData.memory < this.oldInstanceData.memory || this.newInstanceData.memory < this.oldInstanceData.memory
      },
      isExtraStorageDisabled() {
        return this.visibleFields.extraStorage.requiredLVM && !this.entity.useLVM
      },
      areKeysSame() {
        return this.oldEntity.keys.join() === this.entity.keys.join()
      },
      hasExtraStorage() {
        return typeof this.entity.extraStorage !== 'undefined'
      },
      minExtraStorage() {
        return this.entity.constructor.minExtraStorage || 0
      },
      maxExtraStorage() {
        return this.entity.constructor.maxExtraStorage || 0
      },
      isResizeDisabled() {
        const isSame = this.hasExtraStorage && (this.oldEntity.extraStorage === this.entity.extraStorage)
        const isOutOfRange = this.hasExtraStorage && (this.entity.extraStorage < this.minExtraStorage || this.entity.extraStorage > this.maxExtraStorage)
        return this.inProgress || this.isExtraStorageDisabled || isSame || isOutOfRange
      },
    },
    methods: {
      open(entity) {
        this.visible = true
        this.entity = cloneDeep(entity)
        this.reversible = this.visibleFields.instance && this.visibleFields.instance.reversible
      },
      updateInstance() {
        this.loading = true
        utils.reachGoal('UPDATE_HOST_INSTANCE_TRY')
        this.entity.updateInstance(this.entity.instance, this.reversible).then(() => {
          utils.reachGoal('UPDATE_HOST_INSTANCE_SUCCESS')
          this.success = true
          setTimeout(() => {
            this.onSuccess()
          }, 1000)
        }).catch(error => {
          utils.reachGoal('UPDATE_HOST_INSTANCE_FAIL')
          this.apiErrors = error
        }).finally(() => {
          this.loading = false
        })
      },
      updateDisk() {
        this.loading = true
        utils.reachGoal('UPDATE_HOST_DISK_TRY')
        this.entity.updateDisk(this.entity.extraStorage).then(() => {
          utils.reachGoal('UPDATE_HOST_DISK_SUCCESS')
          this.success = true
          setTimeout(() => {
            this.onSuccess()
          }, 1000)
        }).catch(error => {
          utils.reachGoal('UPDATE_HOST_DISK_FAIL')
          this.apiErrors = error
        }).finally(() => {
          this.loading = false
        })
      },
      saveKeys() {
        this.loading = true
        utils.reachGoal('UPDATE_HOST_KEYS_TRY')
        this.entity.saveKeys(this.entity.keys).then(() => {
          utils.reachGoal('UPDATE_HOST_KEYS_SUCCESS')
          this.success = true
          setTimeout(() => {
            this.onSuccess()
          }, 1000)
        }).catch(error => {
          utils.reachGoal('UPDATE_HOST_KEYS_FAIL')
          this.apiErrors = error
        }).finally(() => {
          this.loading = false
        })
      },
    },
    created() {
      this.$modals.updateHost = this
    },
  }
</script>

<style scoped>
  .resize-icons { float: right; }
  .resize-icons .icons { margin-left: .5em; }
  .arrow.icon, .arrows.icon, .lock.icon { font-size: 1em!important; }
  .lock.icon { font-size: .7em!important; }
</style>
